import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    Typography,
    Box,
    IconButton,
    Stack,
    Card,
    CardContent,
    Chip, type SxProps
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Bolt, CloudySnowing, Help, Hive, Power, VerifiedUser } from "@mui/icons-material";
import { green } from "@mui/material/colors";
import { ProductType, type AppProduct, type AvailableGoal } from "@shared/interfaces/product";
import { useText } from "../../main.tsx";
import Tooltip from "../Tooltip/Tooltip.tsx";
import ProductManager from "../../lib/Product/ProductManager.ts";
import { nonProduct } from "../../lib/Product/nonProduct.ts";
import type { ProductByGoal, SelectedProducts } from "../../types/product";
import { FormattedNumber } from "react-intl";

interface Props {
    open?: boolean;
    items: ProductByGoal;
    onClick?: () => void;
    selectedGoal: AvailableGoal;
    selectedProducts: SelectedProducts;
    currentType: ProductType;
    onProductSelect: (selectedProduct: AppProduct) => void;
}

type OverlayText = {
    header: string,
    mainFieldOneTitle: React.ReactNode,
    mainFieldOneSuffix: string,
    mainFieldTwoTitle: React.ReactNode,
    mainFieldTwoSuffix: string | React.ReactNode
};

const overlayTexts = {
    "Solar Panel": {
        header: "Module",
        mainFieldOneTitle: <span><Bolt sx={{ color: green[500] }}></Bolt> Elektrische Leistung</span>,
        mainFieldOneSuffix: " Wp",
        mainFieldTwoTitle: <span><Hive sx={{ color: green[500] }}></Hive> Zellaufbau</span>,
        mainFieldTwoSuffix: <span>Halbzellen</span>
    },
    "Wechselrichter": {
        header: "Wechselrichter",
        mainFieldOneTitle: <span><Bolt sx={{ color: green[500] }}></Bolt>Nennleistung</span>,
        mainFieldOneSuffix: "MPP-Tracker",
        mainFieldTwoTitle: <span><Bolt sx={{ color: green[500] }}></Bolt> MP Module</span>,
        mainFieldTwoSuffix: "% Wirkungsgrad",
    },
    "Battery": {
        header: "Batteriespeicher",
        mainFieldOneTitle: <span><Bolt sx={{ color: green[500] }}></Bolt>Kapazität</span>,
        mainFieldOneSuffix: " kWh pro Modul",
        mainFieldTwoTitle: <span><Bolt sx={{ color: green[500] }}></Bolt>Be- und Entladeleistung</span>,
        mainFieldTwoSuffix: "kW",
    },
    "Accessory": {
        header: "Zubehör",
        mainFieldOneTitle: <span><Help sx={{ color: green[500] }}></Help>Ladeleistung</span>,
        mainFieldOneSuffix: "kW",
        mainFieldTwoTitle: <span><Power sx={{ color: green[500] }}></Power>Kabel / Buchse?</span>,
        mainFieldTwoSuffix: "",
    },
    "default": {
        header: "Produktdetails",
        mainFieldOneTitle: "",
        mainFieldOneSuffix: "",
        mainFieldTwoTitle: "",
        mainFieldTwoSuffix: "",
    }
} as Record<string, OverlayText>;

const getText = (type: string): OverlayText => {
    return overlayTexts[type] || overlayTexts["default"];
};

const selectedSxProps: SxProps = {
    boxShadow: {
        xs: "0px 4px 8px rgba(34, 197, 94, 0.3)",
        sm: "0px 10px 15px rgba(34, 197, 94, 0.3)"
    },
    backgroundColor: "rgba(34, 197, 94, 0.05)",
    border: "2px solid #22c55e"
}

const unselectedSxProps: SxProps = {
    boxShadow: {
        xs: "0px 2px 4px rgba(0,0,0,0.1)",
        sm: "0px 10px 15px rgba(0,0,0,0.1)"
    },
    backgroundColor: "transparent",
    border: "2px solid gray"
}

type ProductsByGoalWithNonProduct = ProductByGoal & {
    none?: AppProduct
};

// @todo Duplicated function (also in ProductItem.tsx)
const getTranslationKeyByType = (type: string): string => {
    switch (type) {
        case ProductType.SOLAR_PANEL:
            return "funnel.wizard.productList.module";
        case ProductType.INVERTER:
            return "funnel.wizard.productList.inverter";
        case ProductType.BATTERY_STORAGE:
            return "funnel.wizard.productList.battery";
        case ProductType.ACCESSORY:
            return "funnel.wizard.productList.accessory";
        case ProductType.SERVICE_PACKAGE:
            return "funnel.wizard.productList.servicePackage";
        default:
            return "funnel.wizard.productList.unknown";
    }
}

const getFullItemList = (items: ProductByGoal, selectedGoal: AvailableGoal, currentProduct: AppProduct) => {
    if (ProductManager.hasGoalNonProduct(selectedGoal, currentProduct.type)) {
        items = {
            none: {
                ...nonProduct, type: currentProduct.type,
            },
            ...items
        } as ProductsByGoalWithNonProduct;
    }

    return items;
}

const ProductOverlay: React.FC<Props> = ({
    open = false,
    onClick,
    items,
    selectedGoal,
    selectedProducts,
    currentType,
    onProductSelect
}) => {
    const { t } = useText();
    const [itemList, setItemList] = useState<ProductsByGoalWithNonProduct>(getFullItemList(items, selectedGoal, selectedProducts[currentType]));
    const [currentProduct, setCurrentProduct] = useState<AppProduct>(selectedProducts[currentType]);

    useEffect(() => {
        setCurrentProduct(selectedProducts[currentType]);
    }, [selectedProducts, currentType]);

    useEffect(() => {
        setItemList(getFullItemList(items, selectedGoal, selectedProducts[currentType]));
    }, [items, selectedGoal, selectedProducts, currentType]);

    const renderProperties = (properties: string | string[] | undefined) => {
        if (!properties) return ' - ';

        if (Array.isArray(properties)) {
            return properties.map((property, idx) => (
                <div key={idx}>{property}</div>
            ));
        }

        if (typeof properties === 'string') {
            try {
                const parsed = JSON.parse(properties);
                if (Array.isArray(parsed)) {
                    return parsed.map((property, idx) => (
                        <div key={idx}>{property}</div>
                    ));
                }
                return <div>{String(parsed)}</div>;
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (error) {
                return <div>{properties}</div>;
            }
        }

        return ' - ';
    };

    const getFormattedPriceDifference = (item: AppProduct, selectedItem: AppProduct) => {
        const difference = item.price - selectedItem.price;

        return <FormattedNumber value={difference} style="currency" currency="EUR" signDisplay="exceptZero"/>;
    };

    return (
        <Dialog
            open={open}
            onClose={onClick}
            fullScreen
            sx={{
                '& .MuiDialog-container': {
                    justifyContent: { xs: 'center', sm: 'flex-end' },
                    alignItems: 'stretch'
                },
                '& .MuiDialog-paper': {
                    width: { xs: '100vw', lg: '50vw', xl: '33.3%' },
                    height: '100vh',
                    maxHeight: '100vh',
                    borderRadius: { xs: 0, sm: 0 },
                    margin: 0,
                    paddingTop: { xs: 'env(safe-area-inset-top)', sm: 0 },
                    paddingBottom: { xs: 'env(safe-area-inset-bottom)', sm: 0 }
                }
            }}
        >
            <DialogContent
                sx={{
                    p: { xs: 2, sm: 3 },
                    height: '100%',
                    overflow: 'auto',
                    margin: { xs: 0, sm: 3 },

                    paddingLeft: { xs: 'max(16px, env(safe-area-inset-left))', sm: 3 },
                    paddingRight: { xs: 'max(16px, env(safe-area-inset-right))', sm: 3 }
                }}
            >
                {/* Header with Close Button */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: { xs: 2, sm: 3 },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 2, sm: 0 },
                        position: 'relative'

                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontFamily: '"Inter", sans-serif',
                                fontWeight: 600,
                                color: "#00031f",
                                fontSize: { xs: '1.5rem', sm: '2rem' },
                                marginTop: { xs: 0, sm: 12 },
                                lineHeight: 1.2
                            }}
                        >
                            {t(`${getTranslationKeyByType(currentProduct.type)}.title`)}
                        </Typography>
                        <Typography sx={{ textDecoration: 'underline', mt: 1 }}>
                            {t(`${getTranslationKeyByType(currentProduct.type)}.description`)}
                            {/*<Help sx={{ color: blue[700], marginLeft: "4px", fontSize: { xs: "18px", sm: "20px" } }} />*/}
                            {t(`${getTranslationKeyByType(currentProduct.type)}.tooltip`) !== null &&
                                <Tooltip text={t(`${getTranslationKeyByType(currentProduct.type)}.tooltip`)}/>
                            }
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={onClick}
                        sx={{
                            alignSelf: { xs: 'flex-end', sm: 'flex-start' },
                            mt: { xs: -2, sm: 0 }
                        }}
                    >
                        <CloseIcon/>
                    </IconButton>
                </Box>

                {/* Product Cards */}
                <Stack spacing={{ xs: 1, sm: 3 }}>
                    {Object.entries(itemList).map(([currentGoal, item], index) => (
                        <div key={index}>
                            {/* Recommended Tag */}
                            {/*{isRecommendedProduct(currentGoal, item) && (*/}
                            {currentGoal === selectedGoal && (
                                <Chip
                                    label="Empfohlen"
                                    size="medium"
                                    sx={{
                                        position: 'relative',
                                        borderRadius: 1.5,
                                        top: { xs: 17, sm: 17 },
                                        left: { xs: 20, sm: 20 },
                                        zIndex: 1,
                                        backgroundColor: '#000000ff',
                                        color: 'white',
                                        mt: -1,
                                    }}
                                />
                            )}
                            <Card
                                key={index}
                                sx={{
                                    ...(item.id === currentProduct.id ? selectedSxProps : unselectedSxProps),
                                    borderRadius: { xs: 1.5, sm: 2.5 }, // Smaller border radius on mobile
                                    cursor: "pointer",
                                    minHeight: { xs: '200px', sm: 'auto' }, // Minimum height on mobile
                                    position: 'relative' // For absolute positioning of recommended tag
                                }}
                                onClick={() => {
                                    setCurrentProduct(item);
                                    onProductSelect(item);
                                }}
                            >
                                <CardContent
                                    sx={{
                                        p: { xs: 2, sm: 3 },
                                        "&:last-child": { pb: { xs: 2, sm: 3 } }
                                    }}
                                >
                                    {/* Product Header */}
                                    <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                fontFamily: '"Inter", sans-serif',
                                                fontWeight: 600,
                                                color: "#00031f",
                                                mb: 1,
                                                lineHeight: 1.2,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: { xs: 'flex-start', sm: 'center' },
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                width: '100%',
                                                fontSize: { xs: '1.2rem', sm: '1.5rem' }, // Smaller font on mobile
                                                gap: { xs: 0.5, sm: 0 }
                                            }}
                                        >
                                            <span>{item.name}</span>
                                            {/* Price difference relative to currently selected item */}
                                            <Typography
                                                sx={{
                                                    fontSize: { xs: "1rem", sm: "1.25rem" },
                                                    fontWeight: "300",
                                                    color: "#747474ff",
                                                    alignSelf: { xs: 'flex-start', sm: 'center' }
                                                }}
                                            >
                                                {getFormattedPriceDifference(item, selectedProducts[currentType])}
                                            </Typography>
                                        </Typography>
                                    </Box>
                                    {currentGoal !== 'none' && (
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                                alignItems: 'flex-start',
                                                rowGap: { xs: 2, sm: 4 },
                                                columnGap: { xs: 0, sm: 1 }
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                component="div"
                                                sx={{
                                                    fontWeight: 400,
                                                    display: 'grid',
                                                }}
                                            >
                                                {getText(item.type).mainFieldOneTitle}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                    <Typography
                                                        variant="h2"
                                                        sx={{
                                                            fontSize: { xs: '1.8rem', sm: '1.8rem' }
                                                        }}
                                                    >
                                                        {item.mainFieldOne ? item.mainFieldOne : '-'}
                                                    </Typography>
                                                    <Typography
                                                        variant="body1"
                                                        component="div"
                                                    >
                                                        {getText(item.type).mainFieldOneSuffix}
                                                    </Typography>
                                                </div>
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                component="div"
                                                sx={{
                                                    display: 'grid',
                                                }}
                                            >
                                                {getText(item.type).mainFieldTwoTitle}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                    <Typography
                                                        variant="h2"
                                                        sx={{
                                                            fontSize: item.mainFieldTwo && item.mainFieldTwo.length > 15
                                                                ? { xs: "0.7rem", sm: "0.9rem" }
                                                                : { xs: "1.5rem", sm: "2rem" }
                                                        }}
                                                    >
                                                        {item.mainFieldTwo ? item.mainFieldTwo : '-'}
                                                    </Typography>
                                                    <Typography variant="body1" component={"span"}>
                                                        {getText(item.type).mainFieldTwoSuffix}
                                                    </Typography>
                                                </div>
                                            </Typography>
                                            <div>
                                                <div style={{ display: "flex", gap: 4, alignItems: 'center' }}>
                                                    <VerifiedUser
                                                        sx={{
                                                            color: green[500],
                                                            fontSize: { xs: '1.125rem', sm: '1.5rem' }
                                                        }}
                                                    ></VerifiedUser>
                                                    <Typography variant="body1" component="div">
                                                        Garantie
                                                    </Typography>
                                                </div>
                                                <Typography
                                                    variant="subtitle1"
                                                >
                                                    {item.guarantee ? item.guarantee : '-'}
                                                </Typography>
                                            </div>
                                            <div>
                                                <div style={{ display: "flex", gap: 4, alignItems: 'center' }}>
                                                    <CloudySnowing
                                                        sx={{
                                                            color: green[500],
                                                        }}
                                                    ></CloudySnowing>
                                                    <Typography
                                                        variant="body1"
                                                        component="div"
                                                    >
                                                        Eigenschaften
                                                    </Typography>
                                                </div>
                                                <Typography variant="subtitle1">
                                                    {renderProperties(item.properties)}
                                                </Typography>
                                            </div>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                    ))}
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default ProductOverlay;